import React, { useState, useEffect } from 'react';
import AgoraUIKit from 'agora-react-uikit';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut, updateProfile, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import './App.css';

const firebaseConfig = {
    apiKey: "AIzaSyCamuShBGWIUC1FOvUcKPp6VBMMrR7N7PI",
    authDomain: "helper-app-aedad.firebaseapp.com",
    projectId: "helper-app-aedad",
    storageBucket: "helper-app-aedad.appspot.com",
    messagingSenderId: "878676863707",
    appId: "1:878676863707:web:b6b22b929a0fd9f18cfafe"
};
const AGORA_APP_ID = '4193fc79e56d4975bbc0388b26abe89d';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

let speechEngine = window.speechSynthesis;
let voices = [];
const loadVoices = () => { voices = speechEngine.getVoices(); };
if (speechEngine.onvoiceschanged !== undefined) { speechEngine.onvoiceschanged = loadVoices; }
const speak = (text) => {
    try {
        speechEngine.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        let selectedVoice = voices.find(v => v.name.includes('Google') && v.name.includes('Female') && v.lang.startsWith('en'));
        if (!selectedVoice) { selectedVoice = voices.find(v => v.name.includes('Female') && v.lang.startsWith('en')); }
        utterance.voice = selectedVoice || voices[0];
        speechEngine.speak(utterance);
    } catch (e) { console.error("Speech synthesis failed", e); }
};

export default function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inCall, setInCall] = useState(false);
    const [callConfig, setCallConfig] = useState(null);

    useEffect(() => {
        loadVoices();
        setPersistence(auth, browserLocalPersistence).then(() => {
            onAuthStateChanged(auth, (currentUser) => {
                setUser(currentUser);
                setLoading(false);
            });
        });
    }, []);

    useEffect(() => {
        if (!user) return;

        const userDocRef = doc(db, "users", user.uid);
        const unsubscribeDb = onSnapshot(userDocRef, (docSnap) => {
            const data = docSnap.data();
            const isCallIncoming = data?.status === 'in-call' && data?.callSession;

            if (isCallIncoming) {
                if (!inCall) { // Only trigger if we aren't already in the call state
                    speak(`Incoming call. Joining now.`);
                    setCallConfig({ roomID: data.callSession.roomID });
                    setInCall(true);
                }
            } else {
                if (inCall) { // If we are in a call, and the status changes to waiting
                    setInCall(false);
                    setCallConfig(null);
                    speak("The call has ended.");
                }
            }
        });
        return () => unsubscribeDb();
    }, [user, inCall]);
    
    const endCallByBlindUser = async () => {
        if (!user) return;
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, { status: 'waiting', callSession: null });
        // The useEffect will handle the rest of the state cleanup
    };

    if (loading) return <div className="app-container"><h2>Loading...</h2></div>
    if (inCall) return <CallComponent user={user} roomID={callConfig.roomID} onEndCall={endCallByBlindUser} />;
    if (user) return <LoggedInScreen user={user} />;
    return <LoginComponent />;
}

const LoginComponent = ({ onAuthSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');

    const handleSignUp = async () => {
        if (!username || !email || !password || !phone) { speak('Please fill all fields.'); return; }
        try {
            const cred = await createUserWithEmailAndPassword(auth, email, password);
            await updateProfile(cred.user, { displayName: username });
            await setDoc(doc(db, "users", cred.user.uid), { uid: cred.user.uid, username, email, phone, role: 'blind_user', status: 'waiting' });
            speak('Account created successfully!');
        } catch (error) { speak(error.message); }
    };
    const handleLogin = () => {
        if (!email || !password) { speak('Please enter email and password.'); return; }
        signInWithEmailAndPassword(auth, email, password).catch(error => speak(error.message));
    };

    return (
        <div className="app-container">
            <div className="card">
                <h2>Create Account or Log In</h2>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Name" onFocus={() => speak('Enter your full name')} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" onFocus={() => speak('Enter your email address')} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" onFocus={() => speak('Enter your password')} />
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone Number" onFocus={() => speak('Enter your phone number')} />
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px'}}>
                    <button onClick={handleLogin} onFocus={() => speak('Log in button')}>Log In</button>
                    <button onClick={handleSignUp} className="secondary" onFocus={() => speak('Sign up button')}>Sign Up</button>
                </div>
            </div>
        </div>
    );
};

const LoggedInScreen = ({ user }) => {
    const statusText = "You are online. Waiting for a call.";
    const handleLogout = () => { speak("Logging out."); signOut(auth); };
    return (
        <div className="loggedin-container">
            <header className="header">
                <div className="header-username" onClick={() => speak(`You are logged in as ${user.displayName}`)}>{user.displayName || user.email}</div>
                <button className="logout-button" onClick={handleLogout} onFocus={() => speak('Log out button')}>Logout</button>
            </header>
            <div className="status-button-container">
                <div className="status-button" onClick={() => speak(statusText)} onFocus={() => speak(`Current status: ${statusText}`)}>
                    {statusText}
                </div>
            </div>
        </div>
    );
};

const CallComponent = ({ user, roomID, onEndCall }) => {
    const connectionData = { appId: AGORA_APP_ID, channel: roomID, token: null, uid: user.uid };
    const callbacks = { EndCall: onEndCall };
    return (
        <div style={{ display: 'flex', width: '100vw', height: '100vh' }}>
            <AgoraUIKit rtcProps={connectionData} callbacks={callbacks} />
        </div>
    );
};